"use client";

import React from "react";
import {useTranslation} from "react-i18next";
import {HandoffActionButton, OpenWorkflowButton} from "./handoffButtons";
import type {HandoffStatus} from "./useWorkflowBuildHandoff";

interface ToolHandoffFooterProps {
   status: HandoffStatus;
   linkedWorkflowId: string | null;
   /** Whether the assistant turn has finished streaming; gates the approve button. */
   canApprove: boolean;
   onApprove: () => void;
   onOpen: (workflowId: string) => void;
}

/**
 * Footer of a callable-tool handoff: the explicit approve button (or a retry
 * after a failed draft), or a link to the draft an earlier approval created.
 */
const ToolHandoffFooter: React.FC<ToolHandoffFooterProps> = ({
   status,
   linkedWorkflowId,
   canApprove,
   onApprove,
   onOpen,
}) => {
   const {t} = useTranslation("ask");

   // Approved earlier (this session or a prior one): link to the draft it
   // created rather than offering to build it again.
   if (linkedWorkflowId) {
      return (
         <div className="px-4 pb-3 pt-1">
            <OpenWorkflowButton onClick={() => onOpen(linkedWorkflowId)} />
         </div>
      );
   }

   if (status === "claimed") {
      return (
         <p className="px-4 pb-3 pt-1 text-xs text-500">
            {t("workspace.buildWorkflow.inProgressElsewhere")}
         </p>
      );
   }

   if (!canApprove) return null;

   return (
      <div className="px-4 pb-3 pt-1 flex flex-col gap-2">
         {status === "error" && (
            <p className="text-xs text-red-500">{t("workspace.buildWorkflow.error")}</p>
         )}
         <HandoffActionButton
            label={
               status === "error"
                  ? t("workspace.buildWorkflow.retry")
                  : t("workspace.buildWorkflow.approve")
            }
            onClick={onApprove}
         />
      </div>
   );
};

export default ToolHandoffFooter;
